import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Dialog,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
  TextField as MuiTextField,
  Chip,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab
} from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { FormProvider, Table, TextField, SelectTag } from '../../../../../components';
import { dropdownFetch, getKeyPathValue, request, toast } from '../../../../../utils';
import CloseIcon from '@mui/icons-material/Close';
import { useLocation } from 'react-router-dom';
import { CustomButtonn } from '../../../../../components';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { BASE_URL } from '../../../../../utils';
import axios from 'axios';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const initialtestDataObj = {
  data: [],
  loading: false
};

// Enhanced Add Test Item Form Component with Reference Ranges
const LisAddTestItemForm = ({ onClose, onAdd, testItemDropdown, isPanel = false }) => {
  const [selectedTestItem, setSelectedTestItem] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [isRequired, setIsRequired] = useState(false);
  const [defaultValue, setDefaultValue] = useState('');
  const [calculationFormula, setCalculationFormula] = useState('');
  const [referenceRanges, setReferenceRanges] = useState([]);

  const handleAddReferenceRange = () => {
    setReferenceRanges(prev => [...prev, {
      gender: 'all',
      age_from: '',
      age_to: '',
      range_from: '',
      range_to: ''
    }]);
  };

  const updateReferenceRange = (index, field, value) => {
    setReferenceRanges(prev => prev.map((range, i) => 
      i === index ? { ...range, [field]: value } : range
    ));
  };

  const removeReferenceRange = (index) => {
    setReferenceRanges(prev => prev.filter((_, i) => i !== index));
  };

  const handleAdd = () => {
    if (!selectedTestItem) {
      toast('Please select a test item', { variant: 'error' });
      return;
    }

    onAdd({
      test_item: selectedTestItem,
      display_name: displayName,
      is_required: isRequired,
      default_value: defaultValue,
      calculation_formula: calculationFormula,
      reference_ranges: referenceRanges.filter(range => 
        range.range_from !== '' && range.range_to !== ''
      )
    });

    // Reset form
    setSelectedTestItem('');
    setDisplayName('');
    setIsRequired(false);
    setDefaultValue('');
    setCalculationFormula('');
    setReferenceRanges([]);
  };

  const selectedTestItemData = testItemDropdown.find(item => item._id === selectedTestItem);

  return (
    <Paper sx={{ p: 3, minWidth: 600, maxHeight: '90vh', overflow: 'auto' }}>
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" fontWeight="bold">
            Add Test Item to Panel
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>

        <FormControl fullWidth>
          <InputLabel>Test Item</InputLabel>
          <Select
            value={selectedTestItem}
            label="Test Item"
            onChange={(e) => setSelectedTestItem(e.target.value)}
          >
            {testItemDropdown.map((item) => (
              <MenuItem key={item._id} value={item._id}>
                {item.name} {item.shortName && `(${item.shortName})`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedTestItemData && (
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                Test Details:
              </Typography>
              <Typography variant="body2">
                <strong>Name:</strong> {selectedTestItemData.name}
              </Typography>
              <Typography variant="body2">
                <strong>Short Name:</strong> {selectedTestItemData.shortName}
              </Typography>
              <Typography variant="body2">
                <strong>Type:</strong> {selectedTestItemData.testType}
              </Typography>
              <Typography variant="body2">
                <strong>Parameters:</strong> {selectedTestItemData.parameters?.length || 0}
              </Typography>
              <Typography variant="body2">
                <strong>Price:</strong> ₹{selectedTestItemData.price}
              </Typography>
            </CardContent>
          </Card>
        )}

        <MuiTextField
          label="Custom Display Name (Optional)"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="Leave empty to use test item name"
        />

        <MuiTextField
          label="Default Value (Optional)"
          value={defaultValue}
          onChange={(e) => setDefaultValue(e.target.value)}
        />

        <MuiTextField
          label="Custom Calculation Formula (Optional)"
          value={calculationFormula}
          onChange={(e) => setCalculationFormula(e.target.value)}
          placeholder="Override the test item's formula"
          multiline
          rows={2}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={isRequired}
              onChange={(e) => setIsRequired(e.target.checked)}
            />
          }
          label="Required Test"
        />

        {/* Reference Ranges Section */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">Reference Ranges (Optional)</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <Button 
                startIcon={<AddIcon />} 
                onClick={handleAddReferenceRange}
                variant="outlined"
                size="small"
              >
                Add Reference Range
              </Button>
              
              {referenceRanges.map((range, index) => (
                <Card key={index} variant="outlined" sx={{ p: 2 }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Gender</InputLabel>
                      <Select
                        value={range.gender}
                        label="Gender"
                        onChange={(e) => updateReferenceRange(index, 'gender', e.target.value)}
                      >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="male">Male</MenuItem>
                        <MenuItem value="female">Female</MenuItem>
                      </Select>
                    </FormControl>
                    
                    <MuiTextField
                      label="Age From"
                      type="number"
                      size="small"
                      value={range.age_from}
                      onChange={(e) => updateReferenceRange(index, 'age_from', e.target.value)}
                      sx={{ width: 100 }}
                    />
                    
                    <MuiTextField
                      label="Age To"
                      type="number"
                      size="small"
                      value={range.age_to}
                      onChange={(e) => updateReferenceRange(index, 'age_to', e.target.value)}
                      sx={{ width: 100 }}
                    />
                    
                    <MuiTextField
                      label="Range From"
                      type="number"
                      size="small"
                      value={range.range_from}
                      onChange={(e) => updateReferenceRange(index, 'range_from', e.target.value)}
                      sx={{ width: 100 }}
                    />
                    
                    <MuiTextField
                      label="Range To"
                      type="number"
                      size="small"
                      value={range.range_to}
                      onChange={(e) => updateReferenceRange(index, 'range_to', e.target.value)}
                      sx={{ width: 100 }}
                    />
                    
                    <IconButton 
                      onClick={() => removeReferenceRange(index)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                </Card>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>

        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button onClick={onClose} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleAdd} variant="contained" disabled={!selectedTestItem}>
            Add to Panel
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};

export const LisTestPanelMaster = () => {
  const [loading, setLoading] = useState(false);
  const [currentAction, setCurrentAction] = useState();
  const [selectedRecord, setSelectedRecord] = useState();
  const [testItems, setTestItems] = useState([]);
  const [panelCalculations, setPanelCalculations] = useState([]);
  const [openTestListDialog, setOpenTestListDialog] = useState(false);
  const [maingroupdropdown, setMaingroupdropdown] = useState([]);
  const [subgroupdropdown, setSubgroupdropdown] = useState([]);
  const [testItemDropdown, setTestItemDropdown] = useState([]);
  const [openQueryDialog, setOpenQueryDialog] = useState(false);
  const [testDataObj, setTestDataObj] = useState(initialtestDataObj);
  const { pathname } = useLocation();
  const lastPathSegment = pathname.split('/').pop();
  const [activeTab, setActiveTab] = useState(0);
  const [editorData, setEditorData] = useState('');
  const [input, setInput] = useState('');
  const [availableParameters, setAvailableParameters] = useState([]);

  const handleEditorChange = (event, editor) => {
    const data = editor.getData();
    setEditorData(data);
  };

  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        panel_name: Yup.string().required('Panel Name Is Required'),
        panel_code: Yup.string().required('Panel Code Is Required'),
        main_group: Yup.string().required('Main Group Is Required'),
        sub_group: Yup.string().required('Sub Group Is Required'),
        price: Yup.number().required('Price Is Required').min(0, 'Price must be positive'),
        turnaround_time: Yup.string(),
        urgent_turnaround_time: Yup.string(),
        sample_type: Yup.string(),
        sample_volume: Yup.string()
      })
    ),
    defaultValues: {
      min_price: 0,
      max_price: 0,
      is_custom_panel: false,
      allow_individual_booking: false,
      display_order: 0,
      type: 'LAB',
      status: 'active',
      sample_type: '',
      sample_volume: '',
      storage_instructions: '',
      special_instructions: '',
      turnaround_time: '',
      urgent_turnaround_time: ''
    },
    mode: 'all'
  });

  const { handleSubmit, setValue, reset, watch } = methods;
  const watchIsCustomPanel = watch('is_custom_panel');

  // Update available parameters when test items change
  useEffect(() => {
    const parameters = [];
    testItems.forEach(item => {
      const testItem = testItemDropdown.find(t => t._id === item.test_item);
      if (testItem?.parameters) {
        testItem.parameters.forEach(param => {
          parameters.push({
            name: param.name,
            label: param.label,
            testName: testItem.name
          });
        });
      }
    });
    setAvailableParameters(parameters);
  }, [testItems, testItemDropdown]);

  useEffect(() => {
    if (selectedRecord) {
      console.log('SELECTED PANEL RECORD', selectedRecord);
      const keyValuePair = getKeyPathValue(selectedRecord, '', [
        'parameters', 'panel_calculations', '_id', '__v', 'createdAt', 'updatedAt'
      ]);
      keyValuePair.forEach(({ key, value }) => setValue(key, value));
      setTestItems(selectedRecord.parameters || []);
      setPanelCalculations(selectedRecord.panel_calculations || []);
      setEditorData(selectedRecord?.description || '');
    } else {
      reset();
      setTestItems([]);
      setPanelCalculations([]);
      setEditorData('');
    }
  }, [selectedRecord, setValue, reset]);

  const getmaingroupdropdown = useCallback(async () => {
    const data = await dropdownFetch('test-main-group');
    setMaingroupdropdown(data);
  }, []);

  const getSubGroupDropdown = useCallback(async () => {
    const data = await dropdownFetch('test-sub-group');
    setSubgroupdropdown(data);
  }, []);

  const getTestItemDropdown = useCallback(async () => {
    try {
      const response = await request('/lis-test-list', {
        method: 'GET',
        query: { limit: 100, status: 'active' }
      });
      if (response.success) {
        setTestItemDropdown(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching test items:', error);
      toast('Error loading test items', { variant: 'error' });
    }
  }, []);

  useEffect(() => {
    getmaingroupdropdown();
    getSubGroupDropdown();
    getTestItemDropdown();
  }, [getmaingroupdropdown, getSubGroupDropdown, getTestItemDropdown]);

  const formSubmit = async (formValues) => {
    setLoading(true);
    let editFlag = false;
    if (selectedRecord && currentAction === 'edit') {
      editFlag = true;
    }

    const payload = {
      ...formValues,
      description: editorData,
      parameters: testItems.map((item, index) => ({
        test_item: item.test_item,
        display_order: item.display_order || index,
        is_required: item.is_required || false,
        default_value: item.default_value || '',
        calculation_formula: item.calculation_formula || '',
        display_name: item.display_name || '',
        reference_ranges: item.reference_ranges || []
      })),
      panel_calculations: panelCalculations.map((calc, index) => ({
        name: calc.name,
        formula: calc.formula,
        depends_on: calc.depends_on || [],
        result_unit: calc.result_unit || '',
        decimal_places: calc.decimal_places || 2
      }))
    };

    const response = await request(editFlag ? '/lis-test-panel-update' : '/lis-test-panel-create', {
      method: editFlag ? 'PUT' : 'POST',
      params: editFlag ? selectedRecord._id : null,
      body: payload
    });

    if (response?.success) {
      toast(`Successfully ${editFlag ? 'updated' : 'created'} Test Panel`, { variant: 'success' });
      setSelectedRecord(response.data);
      setCurrentAction();
      reset();
      setTestItems([]);
      setPanelCalculations([]);
    } else {
      toast(`Failed to ${editFlag ? 'update' : 'create'} Test Panel`, {
        variant: 'error'
      });
    }
    setLoading(false);
  };

  const handleDeleteRecord = async () => {
    setLoading(true);
    const response = await request('/lis-test-panel-delete', {
      method: 'DELETE',
      params: selectedRecord._id
    });
    if (response?.success) {
      toast('Successfully deleted Test panel', { variant: 'success' });
      setSelectedRecord();
    } else {
      toast('Failed to delete Test panel', { variant: 'error' });
    }
    setLoading(false);
  };

  const handleQueryWindow = async () => {
    setOpenQueryDialog(true);
    setTestDataObj({ loading: true });
    const response = await axios.get(`${BASE_URL}/lis-test-panel/list?limit=${10}`);
    if (response?.data?.success) {
      setTestDataObj({ loading: false, data: response.data?.data });
    } else {
      toast('Error in fetching data', { variant: 'error' });
      setTestDataObj({ loading: false });
    }
  };

  const TEST_ITEM_HEADER = [
    {
      header: 'Order',
      accessor: 'display_order',
      width: 80
    },
    {
      header: 'Test Item',
      accessor: (row) => {
        const testItem = testItemDropdown.find(item => item._id === row.test_item);
        return row.display_name || testItem?.name || 'N/A';
      }
    },
    {
      header: 'Original Name',
      accessor: (row) => {
        const testItem = testItemDropdown.find(item => item._id === row.test_item);
        return testItem?.name || 'N/A';
      }
    },
    {
      header: 'Required',
      accessor: (row) => row.is_required ? 'Yes' : 'No',
      width: 100
    },
    {
      header: 'Default Value',
      accessor: 'default_value',
      width: 120
    },
    {
      header: 'Custom Formula',
      accessor: 'calculation_formula',
      render: (row) => row.calculation_formula ? 
        <Chip label="Custom" size="small" color="primary" /> : 
        <Chip label="Default" size="small" variant="outlined" />
    },
    {
      header: 'Reference Ranges',
      accessor: (row) => row.reference_ranges?.length || 0,
      render: (row) => (
        <Chip 
          label={`${row.reference_ranges?.length || 0} ranges`} 
          size="small" 
          variant={row.reference_ranges?.length ? "filled" : "outlined"}
          color={row.reference_ranges?.length ? "primary" : "default"}
        />
      )
    }
  ];

  const CALCULATION_HEADER = [
    {
      header: 'Name',
      accessor: 'name'
    },
    {
      header: 'Formula',
      accessor: 'formula'
    },
    {
      header: 'Unit',
      accessor: 'result_unit'
    },
    {
      header: 'Depends On',
      accessor: (row) => row.depends_on?.join(', ') || 'None'
    },
    {
      header: 'Decimal Places',
      accessor: 'decimal_places',
      width: 120
    }
  ];

  const TABLE_COLUMNS = [
    { header: 'Panel Name', accessor: 'panel_name' },
    { header: 'Panel Code', accessor: 'panel_code' },
    { header: 'Sub Group', accessor: (row) => row?.sub_group?.sub_group },
    { header: 'Main Group', accessor: (row) => row?.main_group?.main_group },
    { 
      header: 'Tests Count', 
      accessor: (row) => row.parameters?.length || 0,
      width: 100
    },
    { 
      header: 'Custom Panel', 
      accessor: (row) => row.is_custom_panel ? 'Yes' : 'No',
      width: 100
    },
    { header: 'Type', accessor: 'type' },
    { header: 'Status', accessor: 'status', sx: { textTransform: 'uppercase' } },
    { header: 'Price', accessor: 'price', render: (row) => `₹${row.price}` },
    { header: 'Created At', accessor: 'createdAt' }
  ];

  const handleActiveTab = (newTabIndex) => {
    setActiveTab(newTabIndex);
  };

  const handleTestSearch = async () => {
    const lowercasedInput = input.toLowerCase().trim();
  
    try {
      const response = await request('/lis-test-panel-list', {
        method: 'GET',
        query: { panel_name: lowercasedInput }
      });
      if (response.success && Array.isArray(response.data)) {
        setTestDataObj({ loading: false, data: response.data });
      } else {
        toast('Error in fetching data', { variant: 'error' });
        setTestDataObj({ loading: false, data: [] });
      }
    } catch (error) {
      console.error('Error during search:', error);
      toast('Error during search', { variant: 'error' });
      setTestDataObj({ loading: false, data: [] });
    }
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
  };

  useEffect(() => {
    handleTestSearch();
  }, [input]);

  const addCalculation = () => {
    setPanelCalculations(prev => [...prev, {
      name: '',
      formula: '',
      depends_on: [],
      result_unit: '',
      decimal_places: 2
    }]);
  };

  const updateCalculation = (index, field, value) => {
    setPanelCalculations(prev => prev.map((calc, i) => 
      i === index ? { ...calc, [field]: value } : calc
    ));
  };

  const removeCalculation = (index) => {
    setPanelCalculations(prev => prev.filter((_, i) => i !== index));
  };

  const getPanelSummary = () => {
    const totalTests = testItems.length;
    const requiredTests = testItems.filter(item => item.is_required).length;
    const customCalculations = panelCalculations.length;
    const testTypes = {};
    let totalIndividualPrice = 0;
    
    testItems.forEach(item => {
      const testItem = testItemDropdown.find(t => t._id === item.test_item);
      if (testItem) {
        // Count test types
        if (testItem.testType) {
          testTypes[testItem.testType] = (testTypes[testItem.testType] || 0) + 1;
        }
        // Calculate individual test prices
        if (testItem.price) {
          totalIndividualPrice += testItem.price;
        }
      }
    });
    
    return {
      totalTests,
      requiredTests,
      customCalculations,
      hasCustomFormulas: testItems.some(item => item.calculation_formula),
      testTypes,
      totalIndividualPrice,
      savings: totalIndividualPrice - watch('price') > 0 ? totalIndividualPrice - watch('price') : 0
    };
  };

  const panelSummary = getPanelSummary();

  // Tab configuration
  const tabLabels = ["Main", "Test Items", "Calculations", "Sample & Timing", "Description"];

  return (
    <>
      <Paper sx={{ p: 0, flex: 1, borderRadius: 0, boxShadow: 'none' }}>
        <Container
          sx={{
            mt: 1,
            border: '0px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            width: '100%',
            maxWidth: 'none',
            minWidth: '100%',
            boxSizing: 'border-box'
          }}
        >
          <FormProvider methods={methods} onSubmit={handleSubmit(formSubmit)}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              sx={{
                mb: 1,
                borderBottom: '0px solid',
                borderColor: 'grey.300'
              }}
            >
              <Typography color="primary" variant="h3" sx={{ fontWeight: 700 }}>
                Test Panel Master
              </Typography>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 3 }} spacing={1}>
                <CustomButtonn
                  id={`${lastPathSegment}Write`}
                  variant="contained"
                  disabled={['edit', 'new'].includes(currentAction)}
                  onClick={() => {
                    setSelectedRecord();
                    setCurrentAction('new');
                    setTestItems([]);
                    setPanelCalculations([]);
                  }}
                >
                  New
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Update`}
                  variant="contained"
                  disabled={['new', 'edit'].includes(currentAction) || !selectedRecord}
                  onClick={() => setCurrentAction('edit')}
                >
                  Edit
                </CustomButtonn>
                <CustomButtonn
                  id={'Submit'}
                  type="submit"
                  variant="contained"
                  disabled={!['edit', 'new'].includes(currentAction) || loading}
                  onClick={handleSubmit(formSubmit)}
                >
                  Save
                </CustomButtonn>
                <CustomButtonn
                  id={'Cancel'}
                  variant="contained"
                  disabled={!['edit', 'new'].includes(currentAction) || loading}
                  onClick={() => {
                    setCurrentAction();
                  }}
                >
                  Cancel
                </CustomButtonn>
                <CustomButtonn
                  id={`isSuperAdmin`}
                  variant="contained"
                  disabled={!selectedRecord || ['new', 'edit'].includes(currentAction) || loading}
                  onClick={handleDeleteRecord}
                >
                  Delete
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Read`}
                  variant="contained"
                  disabled={['new', 'edit'].includes(currentAction)}
                  onClick={handleQueryWindow}
                >
                  Query
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Print`}
                  variant="contained"
                  disabled={!selectedRecord || currentAction}
                >
                  Print
                </CustomButtonn>
                <CustomButtonn id={'Exit'} variant="contained">
                  Exit
                </CustomButtonn>
              </Stack>
            </Stack>

            <Box
              sx={{
                border: '0px solid gray',
                padding: 2,
                borderRadius: '8px',
                width: '100%',
                boxShadow: '0px 2px 10px #0099ff'
              }}
            >
              {/* Enhanced Tab System */}
              <Tabs value={activeTab} onChange={(e, newValue) => handleActiveTab(newValue)} sx={{ mb: 2 }}>
                {tabLabels.map((label, index) => (
                  <Tab key={index} label={label} />
                ))}
              </Tabs>

              <Grid container spacing={4}>
                {activeTab === 0 && (
                  <>
                    <Grid item xs={6} sx={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: '1%' }}>
                      <Stack spacing={0.5} direction="row" alignItems="center">
                        <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Panel Name</Typography>
                        <TextField
                          disabled={!['new', 'edit'].includes(currentAction)}
                          name="panel_name"
                          placeholder="Enter Panel Name"
                          sx={{ flex: 0.7 }}
                        />
                      </Stack>
                      <Stack spacing={0.5} direction="row" alignItems="center">
                        <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Panel Code</Typography>
                        <TextField
                          disabled={!['new', 'edit'].includes(currentAction)}
                          sx={{ flex: 0.7 }}
                          name="panel_code"
                          placeholder="Enter Panel Code"
                        />
                      </Stack>
                      <Stack spacing={0.5} direction="row" alignItems="center">
                        <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Main Group</Typography>
                        <SelectTag
                          disabled={!['new', 'edit'].includes(currentAction)}
                          formControlSX={{ flex: 0.3 }}
                          name="main_group"
                          placeholder="Select Main Group"
                          menus={maingroupdropdown.map((data) => ({ ...data, name: data.main_group }))}
                        />
                        <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Sub Group</Typography>
                        <SelectTag
                          disabled={!['new', 'edit'].includes(currentAction)}
                          formControlSX={{ flex: 0.2 }}
                          name="sub_group"
                          placeholder="Select Sub Group"
                          menus={subgroupdropdown.map((data) => ({ ...data, name: data.sub_group }))}
                        />
                      </Stack>
                      <Stack spacing={0.5} direction="row" alignItems="center">
                        <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Price</Typography>
                        <TextField
                          disabled={!['new', 'edit'].includes(currentAction)}
                          sx={{ flex: 0.3 }}
                          name="price"
                          placeholder="Price"
                          type="number"
                        />
                        <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Min/Max Price</Typography>
                        <TextField
                          disabled={!['new', 'edit'].includes(currentAction)}
                          sx={{ flex: 0.1 }}
                          name="min_price"
                          type="number"
                        />
                        <TextField
                          disabled={!['new', 'edit'].includes(currentAction)}
                          sx={{ flex: 0.1 }}
                          name="max_price"
                          type="number"
                        />
                      </Stack>
                      <Stack spacing={0.5} direction="row" alignItems="center">
                        <FormControlLabel
                          control={
                            <Switch
                              disabled={!['new', 'edit'].includes(currentAction)}
                              name="is_custom_panel"
                            />
                          }
                          label="Custom Panel"
                          sx={{ flex: 0.5 }}
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              disabled={!['new', 'edit'].includes(currentAction)}
                              name="allow_individual_booking"
                            />
                          }
                          label="Allow Individual Booking"
                          sx={{ flex: 0.5 }}
                        />
                      </Stack>
                      <Stack spacing={0.5} direction="row" alignItems="center">
                        <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Display Order</Typography>
                        <TextField
                          disabled={!['new', 'edit'].includes(currentAction)}
                          sx={{ flex: 0.2 }}
                          name="display_order"
                          type="number"
                        />
                        <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Type</Typography>
                        <SelectTag
                          disabled={!['new', 'edit'].includes(currentAction)}
                          formControlSX={{ flex: 0.3 }}
                          name="type"
                          menus={[
                            { _id: 'LAB', name: 'LAB' },
                            { _id: 'RADIOLOGY', name: 'RADIOLOGY' },
                            { _id: 'OTHER', name: 'OTHER' }
                          ]}
                        />
                      </Stack>
                      <Stack spacing={0.5} direction="row" alignItems="center">
                        <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Status</Typography>
                        <SelectTag
                          disabled={!['new', 'edit'].includes(currentAction)}
                          formControlSX={{ flex: 0.7 }}
                          name="status"
                          menus={[
                            { _id: 'active', name: 'Active' },
                            { _id: 'inactive', name: 'Inactive' },
                            { _id: 'archived', name: 'Archived' }
                          ]}
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            Panel Summary
                          </Typography>
                          <Stack spacing={1}>
                            <Stack direction="row" justifyContent="space-between">
                              <Typography>Total Tests:</Typography>
                              <Typography fontWeight="bold">{panelSummary.totalTests}</Typography>
                            </Stack>
                            <Stack direction="row" justifyContent="space-between">
                              <Typography>Required Tests:</Typography>
                              <Typography fontWeight="bold">{panelSummary.requiredTests}</Typography>
                            </Stack>
                            <Stack direction="row" justifyContent="space-between">
                              <Typography>Custom Calculations:</Typography>
                              <Typography fontWeight="bold">{panelSummary.customCalculations}</Typography>
                            </Stack>
                            <Stack direction="row" justifyContent="space-between">
                              <Typography>Individual Test Price:</Typography>
                              <Typography fontWeight="bold">₹{panelSummary.totalIndividualPrice}</Typography>
                            </Stack>
                            <Stack direction="row" justifyContent="space-between">
                              <Typography>Panel Price:</Typography>
                              <Typography fontWeight="bold" color="primary">₹{watch('price') || 0}</Typography>
                            </Stack>
                            {panelSummary.savings > 0 && (
                              <Stack direction="row" justifyContent="space-between">
                                <Typography>Savings:</Typography>
                                <Typography fontWeight="bold" color="success.main">
                                  ₹{panelSummary.savings} ({(panelSummary.savings / panelSummary.totalIndividualPrice * 100).toFixed(1)}%)
                                </Typography>
                              </Stack>
                            )}
                            <Stack direction="row" justifyContent="space-between">
                              <Typography>Panel Type:</Typography>
                              <Typography fontWeight="bold">
                                {watchIsCustomPanel ? 'Custom Panel' : 'Standard Panel'}
                              </Typography>
                            </Stack>
                            {Object.keys(panelSummary.testTypes).length > 0 && (
                              <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                  Test Types:
                                </Typography>
                                {Object.entries(panelSummary.testTypes).map(([type, count]) => (
                                  <Chip 
                                    key={type} 
                                    label={`${type}: ${count}`} 
                                    size="small" 
                                    sx={{ mr: 1, mb: 1 }}
                                    variant="outlined"
                                  />
                                ))}
                              </Box>
                            )}
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  </>
                )}

                {activeTab === 1 && (
                  <Grid item xs={12}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="h6">Test Items in Panel</Typography>
                      <Button
                        startIcon={<AddIcon />}
                        onClick={() => setOpenTestListDialog(true)}
                        variant="contained"
                        disabled={!['edit', 'new'].includes(currentAction)}
                      >
                        Add Test Item
                      </Button>
                    </Stack>
                    <Table
                      data={testItems}
                      columns={TEST_ITEM_HEADER}
                      size="medium"
                      showDeleteAction={true}
                      onDeleteHandler={(id) => {
                        setTestItems(prev => prev.filter(item => item._id !== id));
                      }}
                    />
                  </Grid>
                )}

                {activeTab === 2 && (
                  <Grid item xs={12}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="h6">Panel Calculations</Typography>
                      <Button
                        startIcon={<AddIcon />}
                        onClick={addCalculation}
                        variant="contained"
                        disabled={!['edit', 'new'].includes(currentAction)}
                      >
                        Add Calculation
                      </Button>
                    </Stack>
                    
                    {/* Available Parameters Info */}
                    {availableParameters.length > 0 && (
                      <Card sx={{ mb: 2, bgcolor: 'info.light' }}>
                        <CardContent>
                          <Typography variant="subtitle2" gutterBottom>
                            Available Parameters for Formulas:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {availableParameters.map((param, index) => (
                              <Chip
                                key={index}
                                label={`${param.name} (${param.label})`}
                                size="small"
                                variant="outlined"
                                title={`From: ${param.testName}`}
                              />
                            ))}
                          </Box>
                        </CardContent>
                      </Card>
                    )}
                    
                    {panelCalculations.map((calc, index) => (
                      <Card key={index} sx={{ mb: 2, p: 2 }}>
                        <Stack direction="row" spacing={2} alignItems="flex-start">
                          <IconButton sx={{ mt: 1 }}>
                            <DragIndicatorIcon />
                          </IconButton>
                          <Grid container spacing={2} flex={1}>
                            <Grid item xs={3}>
                              <MuiTextField
                                fullWidth
                                label="Calculation Name"
                                value={calc.name}
                                onChange={(e) => updateCalculation(index, 'name', e.target.value)}
                                disabled={!['edit', 'new'].includes(currentAction)}
                                size="small"
                              />
                            </Grid>
                            <Grid item xs={4}>
                              <MuiTextField
                                fullWidth
                                label="Formula"
                                value={calc.formula}
                                onChange={(e) => updateCalculation(index, 'formula', e.target.value)}
                                disabled={!['edit', 'new'].includes(currentAction)}
                                size="small"
                                placeholder="e.g., (parameter1 + parameter2) / 2"
                              />
                            </Grid>
                            <Grid item xs={2}>
                              <MuiTextField
                                fullWidth
                                label="Unit"
                                value={calc.result_unit}
                                onChange={(e) => updateCalculation(index, 'result_unit', e.target.value)}
                                disabled={!['edit', 'new'].includes(currentAction)}
                                size="small"
                              />
                            </Grid>
                            <Grid item xs={2}>
                              <MuiTextField
                                fullWidth
                                label="Decimal Places"
                                type="number"
                                value={calc.decimal_places}
                                onChange={(e) => updateCalculation(index, 'decimal_places', parseInt(e.target.value) || 2)}
                                disabled={!['edit', 'new'].includes(currentAction)}
                                size="small"
                              />
                            </Grid>
                            <Grid item xs={1}>
                              <IconButton 
                                onClick={() => removeCalculation(index)}
                                disabled={!['edit', 'new'].includes(currentAction)}
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Grid>
                          </Grid>
                        </Stack>
                        {/* Dependencies Section */}
                        <Box sx={{ mt: 2, pl: 7 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Depends On Parameters:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {availableParameters.map((param, paramIndex) => (
                              <Chip
                                key={paramIndex}
                                label={param.name}
                                size="small"
                                variant={calc.depends_on?.includes(param.name) ? "filled" : "outlined"}
                                color={calc.depends_on?.includes(param.name) ? "primary" : "default"}
                                onClick={() => {
                                  const currentDependsOn = calc.depends_on || [];
                                  const newDependsOn = currentDependsOn.includes(param.name)
                                    ? currentDependsOn.filter(dep => dep !== param.name)
                                    : [...currentDependsOn, param.name];
                                  updateCalculation(index, 'depends_on', newDependsOn);
                                }}
                                clickable
                              />
                            ))}
                          </Box>
                        </Box>
                      </Card>
                    ))}

                    {panelCalculations.length > 0 && (
                      <Table
                        data={panelCalculations}
                        columns={CALCULATION_HEADER}
                        size="small"
                      />
                    )}
                  </Grid>
                )}

                {activeTab === 3 && (
                  <Grid item xs={12}>
                    <Stack spacing={3}>
                      <Typography variant="h6">Sample Requirements & Turnaround Time</Typography>
                      
                      <Grid container spacing={3}>
                        <Grid item xs={6}>
                          <Stack spacing={2}>
                            <Typography variant="subtitle1" fontWeight="bold">Sample Information</Typography>
                            <TextField
                              disabled={!['new', 'edit'].includes(currentAction)}
                              name="sample_type"
                              label="Sample Type"
                              placeholder="e.g., Whole Blood, Serum, Urine"
                            />
                            <TextField
                              disabled={!['new', 'edit'].includes(currentAction)}
                              name="sample_volume"
                              label="Sample Volume"
                              placeholder="e.g., 2-3 mL, 5 mL"
                            />
                            <TextField
                              disabled={!['new', 'edit'].includes(currentAction)}
                              name="storage_instructions"
                              label="Storage Instructions"
                              placeholder="e.g., Store at 2-8°C"
                              multiline
                              rows={2}
                            />
                            <TextField
                              disabled={!['new', 'edit'].includes(currentAction)}
                              name="special_instructions"
                              label="Special Instructions"
                              placeholder="e.g., Fasting required, Avoid hemolysis"
                              multiline
                              rows={2}
                            />
                          </Stack>
                        </Grid>
                        
                        <Grid item xs={6}>
                          <Stack spacing={2}>
                            <Typography variant="subtitle1" fontWeight="bold">Turnaround Time</Typography>
                            <TextField
                              disabled={!['new', 'edit'].includes(currentAction)}
                              name="turnaround_time"
                              label="Regular Turnaround Time"
                              placeholder="e.g., 4 hours, 24 hours"
                            />
                            <TextField
                              disabled={!['new', 'edit'].includes(currentAction)}
                              name="urgent_turnaround_time"
                              label="Urgent Turnaround Time"
                              placeholder="e.g., 2 hours, 6 hours"
                            />
                            
                            {/* Current Sample Requirements Summary */}
                            <Card variant="outlined" sx={{ mt: 2 }}>
                              <CardContent>
                                <Typography variant="subtitle2" gutterBottom>
                                  Current Requirements:
                                </Typography>
                                <Stack spacing={0.5}>
                                  <Typography variant="body2">
                                    <strong>Sample Type:</strong> {watch('sample_type') || 'Not specified'}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Volume:</strong> {watch('sample_volume') || 'Not specified'}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Regular TAT:</strong> {watch('turnaround_time') || 'Not specified'}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Urgent TAT:</strong> {watch('urgent_turnaround_time') || 'Not specified'}
                                  </Typography>
                                </Stack>
                              </CardContent>
                            </Card>
                          </Stack>
                        </Grid>
                      </Grid>
                    </Stack>
                  </Grid>
                )}

                {activeTab === 4 && (
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>Panel Description & Interpretation</Typography>
                    <CKEditor
                      editor={ClassicEditor}
                      data={editorData}
                      onChange={handleEditorChange}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>
          </FormProvider>
        </Container>
      </Paper>

      <Dialog open={openTestListDialog} onClose={() => setOpenTestListDialog(false)} scroll="body" maxWidth="md">
        <LisAddTestItemForm
          onClose={() => setOpenTestListDialog(false)}
          onAdd={(data) => {
            const existingItem = testItems.find(item => item.test_item === data.test_item);
            if (existingItem) {
              toast('Test Item already added to panel', { variant: 'error' });
              return;
            }
            
            const testItem = testItemDropdown.find(item => item._id === data.test_item);
            setTestItems(prev => [...prev, {
              ...data,
              _id: data.test_item,
              display_name: data.display_name || testItem?.name,
              display_order: prev.length,
              is_required: data.is_required || false,
              default_value: data.default_value || '',
              calculation_formula: data.calculation_formula || '',
              reference_ranges: data.reference_ranges || []
            }]);
            setOpenTestListDialog(false);
          }}
          testItemDropdown={testItemDropdown}
          isPanel={true}
        />
      </Dialog>

      <Dialog
        open={openQueryDialog}
        onClose={() => setOpenQueryDialog(false)}
        PaperProps={{ sx: { width: '90%', p: 2 } }}
        maxWidth="xl"
        scroll="body"
      >
        <Stack
          padding={0.2}
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          borderBottom="1px solid"
          borderColor="grey.300"
        >
          <Typography variant="h4" fontWeight="bold">
            Test Panel Query Window
          </Typography>
          <IconButton onClick={() => setOpenQueryDialog(false)}>
            <CloseIcon color="error" fontSize="large" />
          </IconButton>
        </Stack>
        <Stack sx={{ mt: 2 }}>
          <MuiTextField
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                padding: '0',
              },
              '& .MuiOutlinedInput-input': {
                padding: '8px 14px'
              }
            }}
            label="Search Panel"
            variant="outlined"
            value={input}
            onChange={handleInputChange}
          />
        </Stack>
        <Table
          sx={{ mt: 2 }}
          columns={TABLE_COLUMNS}
          data={testDataObj.data || []}
          loading={testDataObj.loading}
          onRowClick={(row) => {
            setSelectedRecord(row);
            setOpenQueryDialog(false);
          }}
        />
      </Dialog>
    </>
  );
};